import { ref } from 'vue';
import axios from 'axios';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';
export function useMatterUserManagement() {
  const clientMatterStore = useClientMatterStore();
  const accountsStore = useAccountsStore();
  const userMessages = ref([]);
  const getBillableItemType = selectedLineItem => {
    return selectedLineItem.charges ? 'fixed fee' : selectedLineItem.entries ? 'time entries' : 'expenses';
  };
  const getUserFromGroups = (userId, groups) => {
    return groups.find(g => g.lineItems.some(li => li.account?.id === userId))?.lineItems.find(li => li.account?.id === userId)?.account;
  };
  const getMissingUserIds = (accountIds, existingUsers) => {
    return accountIds.filter(id => !existingUsers.find(u => u.id === id));
  };
  const getRateInfo = async (userId, matterDetails) => {
    // Check if matter has rate groups
    if (matterDetails?.rateGroup?.rateGroupAccounts) {
      const userRateAccount = matterDetails.rateGroup.rateGroupAccounts.find(account => account.accountId === userId);
      if (userRateAccount) {
        return {
          amount: userRateAccount.rate,
          rateGroupName: matterDetails.rateGroup.name
        };
      }
    }

    // Fallback to standard rate
    try {
      const accounts = await accountsStore.listAccounts();
      const accountDetails = accounts.find(account => account.id === userId);
      return {
        amount: accountDetails?.rates?.standard || 0,
        rateGroupName: 'Standard'
      };
    } catch (error) {
      return {
        amount: 0,
        rateGroupName: 'Standard'
      };
    }
  };

  /**
   * Checks if users exist in the destination matter and generates messages 
   * about users that would be added during the matter change operation.
   * Used for preview/validation before actually performing the matter change.
   * 
   * @param {string} matterId - The ID of the destination matter
   * @param {Array} accountIds - IDs of accounts that need access to the matter
   * @param {Object} selectedLineItem - The line item being moved
   * @param {Array} groups - Grouped line items containing user information
   * @returns {Object} Object with hasMissingUsers flag and preview messages
   */
  const checkMissingUsersAndGenerateMessages = async (matterId, accountIds, selectedLineItem, groups) => {
    try {
      const [users, matter] = await Promise.all([clientMatterStore.getMatterUsers(matterId), clientMatterStore.getMatterWithClientById(matterId)]);
      const missingUserIds = getMissingUserIds(accountIds, users);
      const hasMissingUsers = missingUserIds.length > 0;
      if (!hasMissingUsers) {
        return {
          hasMissingUsers: false,
          messages: []
        };
      }

      // Get matter details for rate group info
      const {
        data: matterDetails
      } = await axios.get(`/matters/${matterId}`);
      const billableItem = getBillableItemType(selectedLineItem);
      const messages = [];
      for (const userId of missingUserIds) {
        const user = getUserFromGroups(userId, groups);
        if (user) {
          const rateInfo = await getRateInfo(userId, matterDetails);
          const message = `User ${user.name} was added to matter ${matter.name} with their ${rateInfo.rateGroupName} Rate of $${rateInfo.amount.toFixed(2)} so the ${billableItem} could be moved to matter ${matter.name}`;
          messages.push(message);
        }
      }
      return {
        hasMissingUsers,
        messages
      };
    } catch (error) {
      return {
        hasMissingUsers: false,
        messages: ['Error checking user requirements']
      };
    }
  };

  /**
   * Generates user addition messages and stores them in the userMessages ref.
   * This is called during the actual matter change operation to inform about
   * users that were added to the destination matter.
   * 
   * @param {string} matterId - The ID of the destination matter
   * @param {Array} accountIds - IDs of accounts that need access to the matter
   * @param {Object} selectedLineItem - The line item being moved
   * @param {Array} groups - Grouped line items containing user information
   */
  const generateUserAdditionMessages = async (matterId, accountIds, selectedLineItem, groups) => {
    userMessages.value = [];
    try {
      const [users, matter] = await Promise.all([clientMatterStore.getMatterUsers(matterId), clientMatterStore.getMatterWithClientById(matterId)]);
      const missingUserIds = getMissingUserIds(accountIds, users);
      if (missingUserIds.length === 0) return;
      const {
        data: matterDetails
      } = await axios.get(`/matters/${matterId}`);
      const billableItem = getBillableItemType(selectedLineItem);
      for (const userId of missingUserIds) {
        const user = getUserFromGroups(userId, groups);
        if (user) {
          const rateInfo = await getRateInfo(userId, matterDetails);
          const message = `User ${user.name} was added to matter ${matter.name} with their ${rateInfo.rateGroupName} Rate of $${rateInfo.amount.toFixed(2)} so the ${billableItem} could be moved to matter ${matter.name}`;
          userMessages.value.push(message);
        }
      }
    } catch (error) {
      userMessages.value.push('Error checking user requirements');
    }
  };
  const clearCache = () => {
    userMessages.value = [];
  };
  return {
    userMessages,
    checkMissingUsersAndGenerateMessages,
    generateUserAdditionMessages,
    clearCache
  };
}